import { useEffect, useState } from 'react';
import type { ComponentType, ReactElement, ReactNode } from 'react';
import { useSnackbar } from 'notistack';
import { Navigate, useLocation } from 'react-router-dom';
import { TenantSlugDiscoveryResponse } from 'api';
import {
  IS_LOGGING_OUT_KEY,
  LOGGING_OUT_VALUE,
  SESSION_EXPIRED_MESSAGE,
  SIGN_IN_VIEW,
} from 'common/constants/auth';
import { LoadingState } from 'components/ui';
import { useZoom } from 'context/ZoomContext';
import { useIsUserLoggedIn, useTenantDiscovery, useVerifyAccessToken } from 'pages/Auth/hooks';
import { buildSignInUrl, getStoredAccessToken } from 'pages/Auth/utils';

interface CustomRouteElementProps {
  component?: ComponentType;
  isPrivate?: boolean;
  layout?:
    | ComponentType<{ children: ReactNode }>
    | ((props: { children: ReactNode }) => ReactElement);
}

const CUSTOM_ROUTE_SESSION_LOADING_LABEL = 'Checking your session.';
const CUSTOM_ROUTE_DISCOVERY_LOADING_LABEL = 'Preparing your sign-in.';

// Helper component that returns the element for use in Route components
export const CustomRouteElement = (props: CustomRouteElementProps) => {
  const isUserLoggedIn = useIsUserLoggedIn();
  const verifyAccessToken = useVerifyAccessToken();
  const { enqueueSnackbar } = useSnackbar();
  const { zoomLevel } = useZoom();

  // this state gates the rendering of the prop Component or redirection to LogIn while we determine
  // if tenant discovery is needed and resolve the flow to follow
  const [isPendingFlowResolution, setIsPendingFlowResolution] = useState(true);
  const [isVerifyingStoredSession, setIsVerifyingStoredSession] = useState(false);

  const { discoverTenantBySlug, needsDiscovery } = useTenantDiscovery({ location: globalThis.location.href });

  const [hasVerifiedToken, setHasVerifiedToken] = useState(false);
  const accessToken = getStoredAccessToken();
  const location = useLocation();
  const isLoggingOut = localStorage.getItem(IS_LOGGING_OUT_KEY) === LOGGING_OUT_VALUE;

  const { layout: Layout, component: Component, isPrivate = false } = props;

  const hasAccessToken = Boolean(accessToken);
  const viewportMinHeight = `calc(100vh / ${zoomLevel})`;
  const viewportMinHeightSx = {
    '@supports (min-height: 100dvh)': {
      minHeight: `calc(100dvh / ${zoomLevel})`,
    },
  };

  // Keep route-guard loading outside private layouts so those layouts do not
  // run app bootstrap effects before auth/session checks are resolved.
  const renderBootstrapLoading = (label: string) => (
    <LoadingState label={label} minHeight={viewportMinHeight} sx={viewportMinHeightSx} />
  );

  useEffect(() => {
    // Token storage can outlive in-memory user state after refreshes, so verify before redirecting.
    if (hasAccessToken && !isUserLoggedIn && !hasVerifiedToken) {
      let isMounted = true;

      const verifyStoredSession = async () => {
        setIsVerifyingStoredSession(true);

        try {
          await verifyAccessToken();
        } finally {
          if (isMounted) {
            if (!getStoredAccessToken() && !isLoggingOut) {
              enqueueSnackbar(SESSION_EXPIRED_MESSAGE, {
                variant: 'info',
              });
            }
            setHasVerifiedToken(true);
            setIsVerifyingStoredSession(false);
          }
        }
      };

      verifyStoredSession();

      return () => {
        isMounted = false;
      };
    }

    return undefined;
  }, [enqueueSnackbar, hasAccessToken, hasVerifiedToken, isLoggingOut, isUserLoggedIn, verifyAccessToken]);

  useEffect(() => {
    if (!hasAccessToken) {
      setHasVerifiedToken(false);
      setIsVerifyingStoredSession(false);
    }
  }, [hasAccessToken]);

  useEffect(() => {
    const verifyFlowToFollow = async () => {
      const flow = await discoverTenantBySlug();
      const postDiscoveryFlow = flow?.post_discovery_flow as string | undefined;
      if (flow && postDiscoveryFlow === TenantSlugDiscoveryResponse.post_discovery_flow.SAML) {
        // browser redirect to SSO login URL
        globalThis.location.href = flow.login_url as string;
      } else {
        setIsPendingFlowResolution(false);
      }
    };
    if (!isUserLoggedIn && !hasAccessToken && needsDiscovery && isPendingFlowResolution && !isLoggingOut) {
      verifyFlowToFollow();
    }
  }, [needsDiscovery, hasAccessToken, discoverTenantBySlug, isPendingFlowResolution, isUserLoggedIn, isLoggingOut]);

  useEffect(() => {
    // This executes after we've been redirected to logIn,
    // so we can remove the helper flag
    if (isLoggingOut) {
      localStorage.removeItem(IS_LOGGING_OUT_KEY);
    }
  }, [isLoggingOut]);

  if (needsDiscovery && isPendingFlowResolution && !accessToken && !isLoggingOut) {
    // Tenant discovery can render a neutral public loading state as long as it does not mount
    // private app chrome or trigger unrelated bootstrap requests before choosing SSO vs sign-in.
    return renderBootstrapLoading(CUSTOM_ROUTE_DISCOVERY_LOADING_LABEL);
  }

  if (isPrivate && hasAccessToken && !isUserLoggedIn && (!hasVerifiedToken || isVerifyingStoredSession)) {
    return renderBootstrapLoading(CUSTOM_ROUTE_SESSION_LOADING_LABEL);
  }

  // Protect private page from unauthorized users
  if (!isUserLoggedIn && !accessToken && isPrivate) {
    return (
      <Navigate
        to={buildSignInUrl({
          // Explicit logout should land on a fresh sign-in screen instead of restoring the protected page.
          returnTo: isLoggingOut ? null : `${location.pathname}${location.search}`,
          view: SIGN_IN_VIEW.IDENTIFIER,
        })}
        replace
      />
    );
  }

  if (Layout && Component) {
    return (
      <Layout>
        <Component />
      </Layout>
    );
  }

  return Component ? <Component /> : <></>;
};

// Legacy interface for backward compatibility
interface CustomRouteProps {
  path: string;
  component?: ComponentType;
  isPrivate?: boolean;
  layout?:
    | ComponentType<{ children: ReactNode }>
    | ((props: { children: ReactNode }) => ReactElement);
  exact?: boolean;
}

// This is kept for compatibility but should not be used as direct child of Routes
const CustomRoute = (props: CustomRouteProps) => (
  // This component is deprecated in React Router v6 context
  // Use CustomRouteElement instead
  <CustomRouteElement {...props} />
);
export default CustomRoute;
